import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";

import { apiFetch, API } from "../../services/api";
import { isValidIp, durationLabel, apiErrorMessage, BLOCK_DURATIONS } from "../../helpers/security";

// Modale de blocage d'IP. Un blocage est une action qui se voit en production
// (l'IP set WAF refuse immédiatement le trafic) : d'où l'étape de confirmation
// explicite avant l'envoi, et le motif obligatoire — c'est lui qui permettra de
// relire six mois plus tard pourquoi cette adresse a été coupée.
//
// `ip` pré-remplit le champ (clic « Bloquer » depuis le top IP) mais reste
// modifiable : la même modale sert à la saisie manuelle depuis le journal.
export function BlockIpModal({ isOpen, closeModal, ip, onBlocked }) {
    const [address, setAddress] = useState(ip || "");
    const [duration, setDuration] = useState("24h");
    const [reason, setReason] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const trimmedIp = String(address || "").trim();
    const trimmedReason = String(reason || "").trim();

    const validate = () => {
        if (!isValidIp(trimmedIp)) return "Adresse IP invalide (IPv4 ou IPv6, sans masque /xx).";
        if (trimmedReason === "") return "Le motif est obligatoire.";
        return null;
    };

    const askConfirmation = () => {
        const found = validate();
        if (found) {
            setError(found);
            return;
        }
        setError(null);
        setConfirming(true);
    };

    const submit = () => {
        const found = validate();
        if (found) {
            setError(found);
            setConfirming(false);
            return;
        }

        setSaving(true);
        setError(null);
        apiFetch(API.back + "/security/blocks", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ip: trimmedIp, duration, reason: trimmedReason }),
        })
            .then((res) => {
                // Le back renvoie `{"error":"…"}` en français sur un refus
                // (IP invalide, durée inconnue) : on l'affiche mot pour mot.
                if (!res.ok) {
                    return res.text().then((body) => Promise.reject(new Error(apiErrorMessage(res.status, body))));
                }
                return res.text();
            })
            .then(() => {
                setSaving(false);
                if (onBlocked) onBlocked(trimmedIp);
            })
            .catch((err) => {
                console.log(err);
                setSaving(false);
                setConfirming(false);
                setError("Blocage impossible : " + err.message);
            });
    };

    return (
        <Modal show={isOpen} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <Modal.Title>Bloquer une adresse IP</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error ? <div className="alert alert-danger">{error}</div> : null}

                <Form.Group>
                    <Form.Label>Adresse IP</Form.Label>
                    <Form.Control
                        type="text"
                        value={address}
                        placeholder="Ex : 203.0.113.42"
                        disabled={saving}
                        onChange={(e) => {
                            setAddress(e.target.value);
                            setConfirming(false);
                        }}
                    />
                    <Form.Text className="text-muted">
                        IPv4 ou IPv6, adresse seule (le masque /32 ou /128 est posé côté serveur).
                    </Form.Text>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Durée</Form.Label>
                    {BLOCK_DURATIONS.map((d) => (
                        <Form.Check
                            key={d.value}
                            type="radio"
                            id={"block-duration-" + d.value}
                            name="block-duration"
                            label={d.label}
                            checked={duration === d.value}
                            disabled={saving}
                            onChange={() => {
                                setDuration(d.value);
                                setConfirming(false);
                            }}
                        />
                    ))}
                    <Form.Text className="text-muted">
                        Un blocage temporaire est retiré automatiquement à l'échéance. Un blocage définitif ne part qu'à la main.
                    </Form.Text>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Motif (obligatoire)</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={reason}
                        placeholder="Ex : 4 200 requêtes bloquées par le WAF en 5 min, scan de /.env"
                        disabled={saving}
                        onChange={(e) => {
                            setReason(e.target.value);
                            setConfirming(false);
                        }}
                    />
                </Form.Group>

                {confirming ? (
                    <div className="alert alert-warning mb-0">
                        Confirmer le blocage de <strong>{trimmedIp}</strong> — {durationLabel(duration).toLowerCase()} ? Tout le
                        trafic de cette adresse vers le site et les APIs sera refusé immédiatement.
                    </div>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal} disabled={saving}>
                    Annuler
                </Button>
                {confirming ? (
                    <Button variant="danger" onClick={submit} disabled={saving}>
                        {saving ? "Blocage…" : "Confirmer le blocage"}
                    </Button>
                ) : (
                    <Button variant="primary" onClick={askConfirmation} disabled={saving}>
                        Bloquer
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

export default BlockIpModal;
